import {
  AccessorialCode,
  Optional,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';

import i18n from '@/i18n';

import { EmbeddableLocation } from '../../EmbeddableLocation';
import {
  SHIPMENT_STOP_DROP_OFF_TYPES,
  SHIPMENT_STOP_PICKUP_TYPES,
  ShipmentStopType,
} from '../../shipment/ShipmentStopType';
import { ShipOrderShippingPoint } from '../../ShipOrderShippingPoint';
import { ShippingPoint } from '../../ShippingPoint';
import { ValidShipOrderShippingPoint } from '../ValidShipOrderShippingPoint';

const TYPE = 'RoutePlan';

type ConstructableRoutePlanStop = Partial<RoutePlanStop> &
  Required<Pick<RoutePlanStop, 'shipmentIdentifier' | 'sequenceNumber'>>;

export class RoutePlanStop implements ShippingPoint {
  shipmentIdentifier: number;
  sequenceNumber: number;

  /**
   * If present, the server uses the location associated with these ship order
   * shipping points. {@link location} will be ignored if this is not empty.
   *
   * Note! This is not the list of ship order shipping points associated with
   * this stop. Realistically, it probably is, but that's not what it's here
   * for. It's here to provide a pointer to the location. The transport
   * segments' `pickupStopSequenceNumber` / `dropOffStopSequenceNumber` control
   * the actual ship order to stop relationships.
   *
   * See `RoutePlanStop.java`, which carries the same note and a todo to
   * reconsider the design.
   */
  shipOrderShippingPointIds: number[] = [];

  _stopType?: ShipmentStopType;
  _location?: EmbeddableLocation;
  _requestedAppointmentRange?: ZonedDateTimeRange;

  constructor(opts: ConstructableRoutePlanStop) {
    this.shipmentIdentifier = opts.shipmentIdentifier;
    this.sequenceNumber = opts.sequenceNumber;

    if (opts._stopType ?? opts.stopType) {
      this._stopType = opts._stopType ?? opts.stopType;
    }

    if (opts._location ?? opts.location) {
      this._location = new EmbeddableLocation(opts._location ?? opts.location);
    }

    if (opts._requestedAppointmentRange ?? opts.requestedAppointmentRange) {
      this._requestedAppointmentRange = new ZonedDateTimeRange(
        opts._requestedAppointmentRange ?? opts.requestedAppointmentRange,
      );
    }

    if (opts.shipOrderShippingPointIds) {
      this.shipOrderShippingPointIds = opts.shipOrderShippingPointIds;
    }
  }

  static isRoutePlanStop(location: ShippingPoint): location is RoutePlanStop {
    return location.type === TYPE;
  }

  get type() {
    return TYPE;
  }

  get stopType() {
    return this._stopType;
  }

  set stopType(stopType: Optional<ShipmentStopType>) {
    if (
      stopType !== ShipmentStopType.PICKUP &&
      stopType !== ShipmentStopType.DROP_OFF
    ) {
      console.warn(
        `ProposedShipmentStop does not support stop type ${stopType}`,
      );
      return;
    }

    this._stopType = stopType;
  }

  /**
   * The location of this stop. The server ignores it if
   * {@link shipOrderShippingPointIds} is not empty.
   */
  get location(): EmbeddableLocation {
    return this._location ?? new EmbeddableLocation();
  }

  set location(location: Optional<EmbeddableLocation>) {
    this._location = location;
  }

  get requestedAppointmentRange(): ZonedDateTimeRange {
    return this._requestedAppointmentRange ?? new ZonedDateTimeRange();
  }

  set requestedAppointmentRange(
    requestedAppointmentRange: Optional<ZonedDateTimeRange>,
  ) {
    this._requestedAppointmentRange = requestedAppointmentRange;
  }

  get requestedAppointmentEarliest() {
    return this.requestedAppointmentRange.start;
  }

  get requestedAppointmentLatest() {
    return this.requestedAppointmentRange.end;
  }

  get requestedAccessorials(): AccessorialCode[] {
    return [];
  }

  /**
   * Useful when vue needs a :key for a list, but id or sequence number or the
   * index isn't appropriate
   * (e.g. in a sortablejs list)
   */
  get key(): string {
    return JSON.stringify(this);
  }

  get isInProgress() {
    return false;
  }

  get isPickup() {
    return (
      !!this.stopType && SHIPMENT_STOP_PICKUP_TYPES.includes(this.stopType)
    );
  }

  get isDropOff() {
    return (
      !!this.stopType && SHIPMENT_STOP_DROP_OFF_TYPES.includes(this.stopType)
    );
  }

  get stopLabel(): string {
    const stopName = [
      this.location.locationName,
      this.location.city,
      this.location.state,
    ]
      .filter((x) => !!x?.trim())
      .join(', ');

    const stopNumber = i18n.global.t('common.paramNum', {
      param: i18n.global.t('shipments.stops'),
      num: this.sequenceNumber,
    });

    return [stopNumber, stopName].join(' - ');
  }

  get isPlaceholderStop(): boolean {
    return !this._location;
  }

  /**
   * Point this stop's location at one end of a ship order. See
   * {@link shipOrderShippingPointIds} — this does not say the ship order is
   * picked up or dropped off here, the transport segment does.
   */
  addShipOrderShippingPoint(
    shipOrderShippingPoint: ShipOrderShippingPoint,
  ): void {
    const validShipOrderShippingPoint = new ValidShipOrderShippingPoint(
      shipOrderShippingPoint,
    );

    if (
      !this.isPlaceholderStop &&
      !this.location.hasSameAddress(validShipOrderShippingPoint.location)
    ) {
      throw new Error(
        `Invalid State - stop ${this.sequenceNumber} is not at ${validShipOrderShippingPoint.location.cityState}`,
      );
    }

    this.shipOrderShippingPointIds.push(validShipOrderShippingPoint.id);
  }

  isResolved(_requireBothDates: boolean = false) {
    return false;
  }

  toJSON() {
    // `JSON.stringify` ignores getters so manually add them here if needed
    return {
      sequenceNumber: this.sequenceNumber,
      stopType: this.stopType,
      location: this.location,
      requestedAppointmentRange: this.requestedAppointmentRange,
      shipOrderShippingPointIds: this.shipOrderShippingPointIds,
    };
  }
}
